import { useCallback } from 'react';

import { useToast } from '@chakra-ui/react';

import { hasPermission } from '@enterprise/api';
import { get, SECOND } from '@enterprise/core';
import { useEventListener } from '@enterprise/hooks';
import type { AuthorizeParameter } from '@enterprise/ui';

import { customEvents } from '@/constants/custom-events';
import { useApplicationStore } from '@hooks/application/application-store.hook';

import { useCrossTabAuth } from './cross-tab-auth.hook';
import { useNavigateToLogin } from './navigate-to-login.hook';
import { useSessionHeartbeat } from './session-heartbeat.hook';

export const useRootConfiguration = () => {
  const enterpriseApi = useApplicationStore(({ enterpriseApi }) => {
    return enterpriseApi;
  });
  const setUser = useApplicationStore(({ setUser }) => {
    return setUser;
  });
  const toast = useToast();
  const { navigateToLogin } = useNavigateToLogin();
  const { broadcastLogout } = useCrossTabAuth();
  const handleLogOff = useCallback(
    (_: CustomEvent) => {
      broadcastLogout();
      setUser(undefined);
      navigateToLogin();
    },
    [broadcastLogout, navigateToLogin, setUser]
  );

  const handleForbidden = useCallback(() => {
    toast({
      description: 'You do not have permission to access this resource.',
      duration: 10 * SECOND,
      isClosable: true,
      status: 'error',
      title: 'Forbidden',
    });
  }, [toast]);

  const isAuthorized = useCallback(
    ({ permissions, permissionsStrategy }: AuthorizeParameter) => {
      return hasPermission(enterpriseApi, permissions)
        .then((result) => {
          if (result.failure) {
            return {};
          }

          return (result.data ?? []).reduce<Record<string, boolean>>((accumulator, { hasPermission, name }) => {
            accumulator[name] = hasPermission;

            return accumulator;
          }, {});
        })
        .then((permissionCheck) => {
          switch (permissionsStrategy) {
            case 'all': {
              return Object.values(permissionCheck).every((hasPermission) => {
                return hasPermission;
              });
            }

            case 'any':
              for (const key in permissionCheck) {
                const hasPermission = get(permissionCheck, key, false);

                if (hasPermission) {
                  return true;
                }
              }
              break;
          }

          return false;
        });
    },
    [enterpriseApi]
  );

  useEventListener({
    eventName: customEvents.logOffEvent,
    handler: handleLogOff,
  });
  useEventListener({
    eventName: customEvents.forbidden,
    handler: handleForbidden,
  });

  useSessionHeartbeat();

  return { isAuthorized };
};
